/**
 * wayai eval session delete — delete eval session(s) on a hub
 *
 * Destructive and irreversible. The backend cascade removes the session row, its
 * runs + results, and the eval conversations they created (ConversationDO
 * storage, ClickHouse rows, R2 archives).
 *
 * Usage:
 *   wayai eval session delete <session_id>          # delete one session
 *   wayai eval session delete <session_id> -y       # skip confirmation
 *   wayai eval session delete --all                 # delete every session on the hub
 *   wayai eval session delete --all -y --hub <id>   # target a specific hub, no prompt
 */

import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { confirm, UUID_RE } from '../lib/utils.js';

export async function evalSessionDeleteCommand(args: string[]): Promise<void> {
  // resolveActiveHubId splices `--hub <value>` out of args, so the remaining
  // non-flag positional is unambiguously the session id.
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  const skipConfirm = args.includes('-y') || args.includes('--yes');
  const deleteAll = args.includes('--all');
  const sessionId = args.find((a) => !a.startsWith('-'));

  if (deleteAll && sessionId) {
    console.error('Pass either a session id or --all, not both.');
    process.exit(1);
  }
  if (!deleteAll && !sessionId) {
    console.error('Usage: wayai eval session delete <session_id>');
    console.error('       wayai eval session delete --all');
    process.exit(1);
  }
  if (sessionId && !UUID_RE.test(sessionId)) {
    console.error(`Invalid session id: ${sessionId}`);
    process.exit(1);
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  if (deleteAll) {
    if (!skipConfirm) {
      const confirmed = await confirm(
        'This will permanently delete ALL eval sessions on this hub and their conversations. Continue?',
      );
      if (!confirmed) {
        console.log('Aborted.');
        return;
      }
    }
    const result = await client.deleteAllEvalSessions(hubId);
    console.log(
      `Deleted ${result.data.deleted_sessions} session(s) and ${result.data.deleted_conversations} eval conversation(s).`,
    );
    return;
  }

  if (!skipConfirm) {
    const confirmed = await confirm(
      'This will permanently delete this eval session and its conversations. Continue?',
    );
    if (!confirmed) {
      console.log('Aborted.');
      return;
    }
  }

  try {
    const result = await client.deleteEvalSession(hubId, sessionId!);
    const deletedConversations = result.data?.deleted_conversations ?? 0;
    console.log(`Deleted session ${sessionId} (${deletedConversations} eval conversation(s)).`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.error(`Eval session not found: ${sessionId}`);
      process.exit(1);
    }
    throw err;
  }
}
